import { definedNotNull, Time } from '@df/common';

const parseVoucherId = (id: string): { letter: string; number: number } => {
  const [letter, numberStr] = id.split('-');
  return {
    letter,
    number: parseInt(numberStr, 10),
  };
};

const sortByVoucherIdAsString = (a: string, b: string): number => {
  const voucherA = parseVoucherId(a);
  const voucherB = parseVoucherId(b);

  // First sort by letter
  if (voucherA.letter !== voucherB.letter) {
    return voucherA.letter.localeCompare(voucherB.letter);
  }

  // Then sort by number
  return voucherA.number - voucherB.number;
};

const collator = Intl.Collator('sv', { numeric: true });
function sortByString(a: string, b: string): number {
  if (definedNotNull(a)) {
    return collator.compare(a, b);
  } else {
    return 0;
  }
}

// Comparator that orders items by the position of their key in a reference order.
// Items whose key is not in the order sort last.
function byReferenceOrder<T, K>(order: K[], key: (item: T) => K): (a: T, b: T) => number {
  const rank = (item: T): number => {
    const index = order.indexOf(key(item));
    return index === -1 ? Number.MAX_SAFE_INTEGER : index;
  };
  return (a, b) => rank(a) - rank(b);
}

export function sortBookYears<T extends { bookYear: Time.BookYear }>(
  data: T[],
  order: 'Asc' | 'Desc' = 'Asc',
): T[] {
  return data.sort(({ bookYear: a }, { bookYear: b }) =>
    order === 'Asc' ? Time.BookYear.compare(a, b) : Time.BookYear.compare(b, a),
  );
}

export function sortBookYearStrings(
  data: Time.BookYear[],
  order: 'Asc' | 'Desc' = 'Asc',
): Time.BookYear[] {
  return data.sort((a, b) =>
    order === 'Asc' ? Time.BookYear.compare(a, b) : Time.BookYear.compare(b, a),
  );
}

export { byReferenceOrder, sortByVoucherIdAsString, sortByString };
